import { queryClient } from '@api/queryClient';
import request, { REQUEST_METHODS } from '@api/Request';
import { AQUATIC_RESOURCES_QUERY_KEYS } from '@api/services/aquatic-resources/query';
import { useTableInput } from '@stores';
import { formatCoordFlt } from '@utils/helpers';
import { toast } from 'react-toastify';

import { ArChanges, Geometry } from './types';
import { AquaticResourceEndpoints } from './config';

const getDefaultGeometry = (lat: number, lng: number): Geometry => ({
  type: 'Point',
  coordinates: [formatCoordFlt(lng) || 0, formatCoordFlt(lat) || 0],
});

const apiBaseUrl = import.meta.env.VITE_API_URL;

type SaveArEdits = {
  arChanges: ArChanges;
  requestId: string;
};

const arLimitExceededServerMessage = 'Aquatic Resources exceeded the processing limit for the endpoint.';
const arLimitErrorMessage =
  'You can save up to 3,000 aquatic resources at a time. Please reduce the number of records and try again.';

export const saveArChanges = async ({ arChanges, requestId }: SaveArEdits) => {
  const { addedArs, updatedArs } = arChanges;
  const arTableId = `aquatic-resources-table-${requestId}`;
  const url = `${apiBaseUrl}${AquaticResourceEndpoints.aquaticResources()}`;
  const savePromises: ReturnType<typeof request>[] = [];
  const isDeleteAllSelected = useTableInput.getState().getTableInputSelectAllMode(arTableId);
  const hasChanges = useTableInput.getState().hasTableInputChanges(arTableId);

  const onSuccess = (response: { msg: string }) => {
    const uploadLimitExceeded = response.msg === arLimitExceededServerMessage;

    if (uploadLimitExceeded) {
      toast.error(arLimitErrorMessage, { autoClose: false });
      return;
    }

    if (isDeleteAllSelected) {
      useTableInput.getState().setTableInputPageNumber(arTableId, 1);
    }

    useTableInput.getState().resetTableInput(arTableId);

    queryClient.removeQueries({ queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES, requestId] });
    queryClient.removeQueries({ queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_GEOMETRIES, requestId] });
    queryClient.removeQueries({
      queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_WATERNAMES],
    });
    queryClient.invalidateQueries({
      queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS, requestId],
    });
  };

  useTableInput.getState().setTableInputForceOverwrite(arTableId, hasChanges);

  // UPDATE ------------------------------------------------------------------------------------------------------------
  if (updatedArs.length > 0) {
    const mutation = queryClient.getMutationCache().build(queryClient, {
      mutationKey: [AQUATIC_RESOURCES_QUERY_KEYS.UPDATE_AQUATIC_RESOURCES],
      mutationFn: (data) => request({ data, method: REQUEST_METHODS.PUT, url }),
      onSuccess,
    });

    savePromises.push(mutation.execute(updatedArs));
  }

  // ADD ---------------------------------------------------------------------------------------------------------------
  if (addedArs.length > 0) {
    // fill in missing geometries for manually added ARs
    addedArs.forEach((ar) => {
      if (!ar.geometry) {
        ar.geometry = getDefaultGeometry(ar.latitude, ar.longitude);
      }
    });

    const mutation = queryClient.getMutationCache().build(queryClient, {
      mutationKey: [AQUATIC_RESOURCES_QUERY_KEYS.ADD_AQUATIC_RESOURCES],
      mutationFn: (data) => {
        return request({
          data,
          method: REQUEST_METHODS.POST,
          url,
        });
      },
      onSuccess,
    });

    savePromises.push(mutation.execute(addedArs));
  }

  if (!hasChanges) {
    onSuccess({
      msg: 'No changes to save - trigger onSuccess actions to clean-up table input state (unsaved but deleted records, etc)',
    });
  }

  await Promise.all(savePromises);
};

export const saveArDeletes = async ({ arChanges, requestId }: SaveArEdits) => {
  const { addedArs, deletedArs, deleteAllArExclusions, updatedArs } = arChanges;
  const arTableId = `aquatic-resources-table-${requestId}`;
  const deletedArIds = deletedArs.map((ar) => ar.aquaticResourceID);
  const url = `${apiBaseUrl}${AquaticResourceEndpoints.aquaticResources()}`;
  const savePromises: ReturnType<typeof request>[] = [];
  const isDeleteAllSelected = useTableInput.getState().getTableInputSelectAllMode(arTableId);

  const onSuccess = () => {
    if (isDeleteAllSelected) {
      useTableInput.getState().setTableInputPageNumber(arTableId, 1);
    }

    useTableInput.getState().resetTableInput(arTableId);

    queryClient.removeQueries({ queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES, requestId] });
    queryClient.removeQueries({ queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_GEOMETRIES, requestId] });
    queryClient.removeQueries({
      queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_WATERNAMES],
    });
    queryClient.invalidateQueries({
      queryKey: [AQUATIC_RESOURCES_QUERY_KEYS.GET_AQUATIC_RESOURCES_VALIDATIONS, requestId],
    });
  };

  const hasChanges = deletedArIds.length > 0 || isDeleteAllSelected;
  useTableInput.getState().setTableInputForceOverwrite(arTableId, hasChanges);

  // If edits or new records have been created, save these changes since they will be cleared from the UI once the cache is cleared
  if (addedArs.length > 0 || updatedArs.length > 0) {
    saveArChanges({ arChanges, requestId });
  }

  // DELETE ------------------------------------------------------------------------------------------------------------
  if (!isDeleteAllSelected && deletedArIds.length > 0) {
    const mutation = queryClient.getMutationCache().build(queryClient, {
      mutationKey: [AQUATIC_RESOURCES_QUERY_KEYS.DELETE_AQUATIC_RESOURCES],
      mutationFn: (data) => {
        return request({
          data,
          method: REQUEST_METHODS.DELETE,
          url,
        });
      },
      onSuccess,
    });

    savePromises.push(mutation.execute(deletedArIds));
  }

  // DELETE ALL --------------------------------------------------------------------------------------------------------
  if (isDeleteAllSelected) {
    const excludedArIds = deleteAllArExclusions.map((ar) => ar.aquaticResourceID);
    const mutation = queryClient.getMutationCache().build(queryClient, {
      mutationKey: [AQUATIC_RESOURCES_QUERY_KEYS.DELETE_ALL_AQUATIC_RESOURCES],
      mutationFn: (data) => {
        return request({
          data,
          method: REQUEST_METHODS.DELETE,
          url: `${apiBaseUrl}${AquaticResourceEndpoints.deleteAllAquaticResources()}`,
        });
      },
      onSuccess,
    });

    savePromises.push(mutation.execute(excludedArIds));
  }

  if (!hasChanges) {
    onSuccess();
  }

  await Promise.all(savePromises);
};
